import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AuthContext } from "context";
import dayjs from "dayjs";
import "dayjs/locale/it";
import { createMRTColumnHelper } from "material-react-table";
import toast from "react-hot-toast";
import MDButton from "../../components/MDButton";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import apiService from "../../services/api-service";
import GenericTable from "../genericTable";
import showToast from "../../utils/toast";

let filesContent;

const SteelSampleManagment = () => {
	const navigate = useNavigate();
	const authContext = useContext(AuthContext);
	const { id } = useParams();
	const [loading, setLoading] = useState(false);
	const [withdrawalOptions, setWithdrawalOptions] = useState([]);
	const [samples, setSamples] = useState([]);
	const [open, setOpen] = useState(false);
	const [message, setMessage] = useState("");
	const [severity, setSeverity] = useState("info");
	const [selectedWithdrawal, setSelectedWithdrawal] = useState({});
	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedSampleId, setSelectedSampleId] = useState(null);
	const [resultDate, setResultDate] = useState(null);
	const [resultFile, setResultFile] = useState(null);

	const columnHelper = createMRTColumnHelper();

	const handleSetNotification = useCallback((msg, severityType) => {
		showToast(msg, severityType);
	}, []);

	const handleClose = (event, reason) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	const handleOpenDeliveryDialog = (sampleId) => {
		setSelectedSampleId(sampleId);
		setDialogOpen(true);
	};

	const handleCloseDeliveryDialog = () => {
		setDialogOpen(false);
		setSelectedSampleId(null);
		setResultDate(null);
		setResultFile(null);
	};

	const handleFileChange = (event) => {
		if (!event.target.files) return;

		const filePromise = new Promise((resolve) => {
			const reader = new FileReader();
			reader.onloadend = () => {
				const fileObj = {
					name: event.target.files[0].name,
					type: event.target.files[0].type,
					size: event.target.files[0].size,
					modified: event.target.files[0].lastModified,
					content: null,
				};
				const contentObj = reader.result;

				filesContent = contentObj;

				resolve(fileObj);
			};
			reader.readAsDataURL(event.target.files[0]);
		});

		Promise.resolve(filePromise).then((processedFile) => {
			setResultFile(processedFile);
		});
	};

	const handleSubmit = async () => {
		if (!resultDate || !resultFile || !selectedSampleId) {
			handleSetNotification("Per favore, inserisci tutti i campi richiesti", "error");
			return;
		}

		const data = {
			document: {
				file: {
					...resultFile,
					content: filesContent,
				},
				date: dayjs(resultDate).format("YYYY-MM-DD"),
			},
			sample_id: selectedSampleId,
		};

		try {
			await apiService.post("/steel-sample-result/", {}, data);
			handleSetNotification("Risultato salvato con successo", "success");
			handleCloseDeliveryDialog();
			fetchSamples();
		} catch (error) {
			handleSetNotification("Errore durante il salvataggio del risultato", "error");
		}
	};

	const handleDelete = (id) => {
		if (window.confirm("Sei sicuro di voler eliminare questo risultato?\nAttenzione questa operazione è irreversibile.")) {
			deleteResource(id, "/steel-sample-result/")
				.then(() => {
					handleSetNotification("Risultato eliminato con successo", "success");
					fetchSamples();
				})
				.catch((error) => {
					handleSetNotification("Si è verificato un errore durante l'eliminazione del risultato. Riprova più tardi se l'errore persiste contatta il supporto tecnico indicato l'errore #00032", "error");
				});
		}
	};

	async function deleteResource(id, endpoint) {
		try {
			const params = { id: id };
			const headers = {};

			const response = await apiService.delete(endpoint, params, headers);

			console.log(`Risorsa con id ${id} eliminata con successo:`, response);

			return response;
		} catch (error) {
			console.error(`Errore durante l'eliminazione della risorsa con id ${id}:`, error.message);

			if (error.message.includes("404")) {
				console.log(`La risorsa con id ${id} non è stata trovata`);
			} else if (error.message.includes("403")) {
				console.log(`Non hai i permessi per eliminare la risorsa con id ${id}`);
			}

			throw error;
		}
	}

	useEffect(() => {
		apiService
			.get("/steel-withdrawal-registration-managment/get-option/", { id_area: localStorage.getItem("areaSelected") }, {})
			.then((response) => {
				if (response) {
					setWithdrawalOptions(response);
					if (id) {
						setSelectedWithdrawal(response.find((x) => x.id == id));
					}
				}
			})
			.catch((error) => {
				handleSetNotification("Errore durante la chiamata al server, codice d'errore #08514262429906", "error");
			});
	}, [id, handleSetNotification]);

	const fetchSamples = useCallback(async () => {
		if (!selectedWithdrawal?.id) return;

		setLoading(true);
		apiService
			.get("/steel-sample-managment/", { id_withdrawal: selectedWithdrawal.id, name: selectedWithdrawal.name }, {})
			.then((response) => {
				if (response) {
					setSamples(response);
				}
			})
			.catch((error) => {
				handleSetNotification("Errore durante la chiamata al server, codice d'errore #08514262429906", "error");
			})
			.finally(() => {
				setLoading(false);
			});
	}, [selectedWithdrawal, handleSetNotification]);

	useEffect(() => {
		if (selectedWithdrawal?.id) {
			fetchSamples();
		}
	}, [selectedWithdrawal, fetchSamples]);

	const columns = [
		columnHelper.accessor("processing_name", {
			header: "Nome processo",
			size: 80,
			enableSorting: false,
			enableColumnFilterModes: false,
			enableColumnFilter: false,
		}),
		columnHelper.accessor("date", {
			header: "Data prelievo",
			size: 80,
			enableSorting: false,
			enableColumnFilterModes: false,
			enableColumnFilter: false,
		}),
		columnHelper.accessor("steel_type", {
			header: "Tipo di acciaio",
			size: 80,
			enableSorting: false,
			enableColumnFilterModes: false,
			enableColumnFilter: false,
		}),
		columnHelper.accessor("sample_identification", {
			header: "Codice Campione",
			size: 80,
			enableSorting: true,
			enableColumnFilterModes: true,
			enableColumnFilter: true,
		}),
		columnHelper.accessor("required_test", {
			header: "Prove richieste",
			size: 80,
			enableSorting: true,
			enableColumnFilterModes: true,
			enableColumnFilter: true,
		}),
		columnHelper.accessor("diameter", {
			header: "Diametro [mm]",
			size: 80,
			enableSorting: true,
			enableColumnFilterModes: true,
			enableColumnFilter: true,
		}),
		columnHelper.accessor("description", {
			header: "Descrizione",
			size: 80,
			enableSorting: true,
			enableColumnFilterModes: true,
			enableColumnFilter: true,
		}),
		columnHelper.accessor("position", {
			header: "Posizione in opera",
			size: 80,
			enableSorting: true,
			enableColumnFilterModes: true,
			enableColumnFilter: true,
		}),
		columnHelper.accessor("url", {
			header: "Risultato",
			size: 80,
			enableSorting: true,
			enableColumnFilterModes: true,
			enableColumnFilter: true,
			Cell: ({ cell, row }) => {
				const url = cell.getValue();

				if (!url && authContext?.hasAuth(`UPDATE_STEELSAMPLERESULT`)) {
					return (
						<center>
							<Tooltip title="Aggiungi risultato">
								<MDButton
									onClick={() => handleOpenDeliveryDialog(row.original.id)}
									size="large"
									variant="outlined"
									sx={{
										minWidth: "auto",
										padding: "4px 8px",
										borderRadius: "6px",
										color: "success.main",
										"&:hover": {
											backgroundColor: "success.main",
											color: "white!important",
										},
									}}
								>
									<AddCircleIcon sx={{ fontSize: 30 }} />
								</MDButton>
							</Tooltip>
						</center>
					);
				}

				if (url) {
					return (
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<MDButton
								onClick={() => window.open(url, "_blank")}
								size="small"
								sx={{
									padding: "8px",
									color: "black",
									boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
								}}
							>
								Scarica
							</MDButton>
							{authContext?.hasAuth(`DELETE_STEELSAMPLERESULT`) && (
								<Tooltip title="Elimina risultato">
									<IconButton
										onClick={() => handleDelete(row.original.id_result)}
										size="medium"
										sx={{
											color: "error.main",
											padding: "4px",
											"&:hover": {
												backgroundColor: "error.lighter",
											},
										}}
									>
										<DeleteOutlineIcon sx={{ fontSize: 20 }} />
									</IconButton>
								</Tooltip>
							)}
						</Box>
					);
				}

				return null;
			},
		}),
	];

	return (
		<DashboardLayout>
			<DashboardNavbar />
			{!loading ? (
				<>
					<center>
						{id && (
							<MDButton onClick={() => navigate("/steel-withdrawal-registration-managment")} size="small">
								Torna alla tabella
							</MDButton>
						)}
						<Typography variant="h4" sx={{ marginTop: "20px" }}>
							ELENCO CAMPIONI
						</Typography>
						<Autocomplete
							disablePortal
							id="combo-box-demo"
							options={withdrawalOptions}
							value={selectedWithdrawal}
							getOptionLabel={(option) => option.name || ""}
							sx={{ width: 300, mt: "20px", mb: "20px" }}
							renderInput={(params) => <TextField {...params} label="Seleziona prelievo" />}
							onChange={(event, newValue) => {
								setSelectedWithdrawal(newValue);
							}}
						/>
						{samples?.length > 0 ? (
							<GenericTable data={samples} columns={columns} authModelName="STEELSAMPLE" />
						) : (
							<Typography variant="body1" sx={{ marginTop: "20px" }}>
								Seleziona un prelievo per visualizzare i campioni
							</Typography>
						)}
					</center>
				</>
			) : (
				<center>
					<CircularProgress size={120} sx={{ marginTop: "250px", color: "#2b84ea" }} />
				</center>
			)}
			<Dialog open={dialogOpen} onClose={handleCloseDeliveryDialog}>
				<DialogTitle>Aggiungi risultato</DialogTitle>
				<DialogContent>
					<Box sx={{ display: "flex", alignItems: "center", gap: 3, flexDirection: "column", mt: 2, width: "400px" }}>
						<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
							<DatePicker label="Data *" value={resultDate} onChange={(newValue) => setResultDate(newValue)} renderInput={(params) => <TextField {...params} fullWidth sx={{ mb: 2, mt: 2 }} required />} />
						</LocalizationProvider>
						<input accept="*/*" style={{ display: "none" }} id="raised-button-file" type="file" onChange={handleFileChange} />
						<label htmlFor="raised-button-file">
							<Button variant="contained" component="span" sx={{ color: "white!important" }}>
								{resultFile ? "Cambia file" : "Carica file"}
							</Button>
						</label>
						{resultFile && (
							<Typography variant="body2" sx={{ mt: 1 }}>
								{resultFile.name}
							</Typography>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDeliveryDialog}>Annulla</Button>
					<Button onClick={handleSubmit} disabled={!resultDate || !resultFile}>
						Salva
					</Button>
				</DialogActions>
			</Dialog>
		</DashboardLayout>
	);
};

export default SteelSampleManagment;
